import { useState, useEffect, useCallback } from 'react'
import { DeseretBook } from '../../../dbcom/api'
import { Logger } from '../../../dbcom'

const logger = new Logger({ context: 'product/reviews/useUserReview' })

/**
 * Gets the user review for a product
 * @param {number} userId - needed for getting the users review
 * @param {number} productId - used for finding the review for the product
 * @returns {object}
 * userReview - The users review for the product or undefined
 * createOrUpdateReview - Creates or Updates a user review
 * errorMessage - The error message if there is one
 */
export const useUserReview = (userId, productId) => {
  const [userReview, setUserReview] = useState()

  /**
   * Returns the review object if the user has submitted a review for the product
   */
  const getUserReview = useCallback(async () => {
    try {
      const userReviews = await DeseretBook.user({ id: userId })
        .review()
        .all()

      setUserReview(
        userReviews.reviews.filter(review => review.productId === productId)[0],
      )
    } catch (error) {
      logger.error(`Unable to get user product review: ${error}`)
    }
  })

  /**
   * Creates or Updates the users product review
   * @param {object} review - the new review object
   */
  const createOrUpdateReview = async review => {
    try {
      let updatedReview

      if (userReview) {
        updatedReview = await DeseretBook.product({ id: productId })
          .review({ id: userReview.id })
          .update(review)
      } else {
        updatedReview = await DeseretBook.product({ id: productId })
          .review()
          .create(review)
      }

      setUserReview(updatedReview)

      return updatedReview
    } catch (error) {
      logger.error(
        `Unable to ${userReview ? 'Update' : 'Create'} star only review.`,
      )

      throw new Error(error)
    }
  }

  useEffect(() => {
    if (!userReview) {
      getUserReview()
    }
  }, [userReview])

  return [userReview, createOrUpdateReview]
}
